import { useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  FileText,
  GraduationCap,
  Building2,
  UserPlus,
  CreditCard,
  Cpu,
  BarChart3,
  PanelLeftClose,
  TrendingUp,
  FlaskConical,
  Boxes,
  type LucideIcon,
} from 'lucide-react'
import { useOrgContext } from '../state/OrgContext'
import { BrandMark } from './BrandMark'

interface SidebarSection {
  key: string
  label: string
  items: { path: string; label: string; icon: LucideIcon }[]
}

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export const Sidebar = ({ collapsed, onToggle }: SidebarProps) => {
  const { activeRole, isInstitution } = useOrgContext()
  const { t } = useTranslation()

  const sections: SidebarSection[] = useMemo(() => {
    const dashboard: SidebarSection = {
      key: 'dashboard',
      label: t('nav.sections.dashboard'),
      items: [
        { path: '/exams', label: t('nav.items.exams'), icon: FileText },
      ],
    }

    const managementItems = [
      { path: '/classes', label: t('nav.items.classes'), icon: GraduationCap },
      ...(isInstitution ? [{ path: '/members', label: t('nav.items.members'), icon: UserPlus }] : []),
      { path: '/analytics', label: t('nav.items.analytics'), icon: BarChart3 },
    ]

    const management: SidebarSection = {
      key: 'management',
      label: t('nav.sections.management'),
      items: managementItems,
    }

    const settings: SidebarSection = {
      key: 'settings',
      label: t('nav.sections.settings'),
      items: [{ path: '/costs', label: t('nav.items.costs'), icon: CreditCard }],
    }

    const admin: SidebarSection = {
      key: 'admin',
      label: t('nav.sections.admin'),
      items: [
        { path: '/subscribers', label: t('nav.items.subscribers'), icon: Building2 },
        { path: '/admin/packages', label: t('nav.items.packages'), icon: Boxes },
        { path: '/llm-settings', label: t('nav.items.llmSettings'), icon: Cpu },
        { path: '/admin/stats', label: t('nav.items.stats'), icon: TrendingUp },
        { path: '/admin/test', label: t('nav.items.aiTestLab'), icon: FlaskConical },
        { path: '/lab', label: t('nav.items.gradingLab'), icon: FlaskConical },
      ],
    }

    if (activeRole === 'ADMIN') {
      return [admin]
    }
    if (activeRole === 'ORG_MANAGER') {
      return [dashboard, management, settings]
    }
    // Teacher: just dashboard + limited management (no analytics, no members, no costs)
    return [
      dashboard,
      {
        key: 'management',
        label: t('nav.sections.management'),
        items: [
          { path: '/classes', label: t('nav.items.classes'), icon: GraduationCap },
        ],
      },
    ]
  }, [activeRole, isInstitution, t])

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        <BrandMark size={32} className="sidebar-logo-mark" alt="" />
        <span className="sidebar-logo-text">{t('appName')}</span>
      </div>

      <nav className="sidebar-nav">
        {sections.map((section) => (
          <div key={section.key} className="sidebar-section">
            <span className="sidebar-section-label">{section.label}</span>
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-nav-item ${isActive ? 'active' : ''}`
                }
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-toggle">
        <button className="sidebar-toggle-btn" onClick={onToggle}>
          <PanelLeftClose size={20} />
          <span>{t('nav.collapse')}</span>
        </button>
      </div>
    </aside>
  )
}
